import { supabase } from '../lib/supabase';

/**
 * Service functions for Clinical Recommendations
 * Handles recommendation history tracking per user account
 */

// =================================================================
// RECOMMENDATION HISTORY
// =================================================================

/**
 * Save recommendation to history (per-account basis)
 * @param {string} userId - User ID
 * @param {Object} recommendationData - Recommendation details
 * @returns {Promise<Object>} Saved recommendation
 */
export const saveRecommendation = async (userId, recommendationData) => {
  try {
    const { data, error } = await supabase?.from('clinical_recommendations')?.insert([{
        user_id: userId,
        // Patient demographics
        patient_id: recommendationData?.patientId,
        patient_age: recommendationData?.patientAge,
        patient_gender: recommendationData?.patientGender,
        weight: recommendationData?.weight,
        is_emergency: recommendationData?.isEmergency,
        is_pregnant: recommendationData?.isPregnant,
        
        // Clinical presentation
        chief_complaint: recommendationData?.chiefComplaint,
        symptom_duration: recommendationData?.symptomDuration,
        severity: recommendationData?.severity,
        clinical_history: recommendationData?.clinicalHistory,
        examination_findings: recommendationData?.examinationFindings,
        body_region: recommendationData?.bodyRegion,
        clinical_presentation: recommendationData?.clinicalPresentation,
        
        // AI recommendation results
        primary_modality: recommendationData?.primaryModality,
        recommended_modality: recommendationData?.recommendedModality || recommendationData?.primaryModality || 'N/A',
        modality_rationale: recommendationData?.modalityRationale,
        clinical_rationale: recommendationData?.clinicalRationale,
        reasoning: recommendationData?.reasoning || recommendationData?.clinicalRationale || 'N/A',
        
        // Evidence and guidelines
        evidence_grade: recommendationData?.evidenceGrade,
        urgency: recommendationData?.urgency,
        confidence_score: recommendationData?.confidenceScore || 0.85,
        alternative_modalities: recommendationData?.alternativeModalities || [],
        guidelines_referenced: recommendationData?.guidelinesReferenced || [],
        irefer_guidelines: recommendationData?.iReferGuidelines || [],
        
        // Safety and radiation
        radiation_dose: recommendationData?.radiationDose,
        contrast_required: recommendationData?.contrastRequired || false,
        contrast_details: recommendationData?.contrastDetails,
        safety_warnings: recommendationData?.safetyWarnings || [],
        
        // iRefer integration
        irefer_code: recommendationData?.ireferCode,
        irefer_url: recommendationData?.ireferUrl,
        irefer_match_confidence: recommendationData?.ireferConfidence,
        
        // Complete AI response for full detail view
        full_ai_response: recommendationData?.fullAiResponse || recommendationData,
        
        // Credits
        credits_used: recommendationData?.creditsUsed || 1
      }])?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      // Patient demographics
      patientId: data?.patient_id,
      patientAge: data?.patient_age,
      patientGender: data?.patient_gender,
      weight: data?.weight,
      isEmergency: data?.is_emergency,
      isPregnant: data?.is_pregnant,
      // Clinical presentation
      chiefComplaint: data?.chief_complaint,
      symptomDuration: data?.symptom_duration,
      severity: data?.severity,
      clinicalHistory: data?.clinical_history,
      examinationFindings: data?.examination_findings,
      bodyRegion: data?.body_region,
      clinicalPresentation: data?.clinical_presentation,
      // AI results
      primaryModality: data?.primary_modality,
      recommendedModality: data?.recommended_modality,
      modalityRationale: data?.modality_rationale,
      clinicalRationale: data?.clinical_rationale,
      reasoning: data?.reasoning,
      // Evidence
      evidenceGrade: data?.evidence_grade,
      urgency: data?.urgency,
      confidenceScore: data?.confidence_score,
      alternativeModalities: data?.alternative_modalities,
      guidelinesReferenced: data?.guidelines_referenced,
      ireferGuidelines: data?.irefer_guidelines,
      // Safety
      radiationDose: data?.radiation_dose,
      contrastRequired: data?.contrast_required,
      contrastDetails: data?.contrast_details,
      safetyWarnings: data?.safety_warnings,
      // iRefer
      ireferCode: data?.irefer_code,
      ireferUrl: data?.irefer_url,
      ireferConfidence: data?.irefer_match_confidence,
      // Full response
      fullAiResponse: data?.full_ai_response,
      creditsUsed: data?.credits_used,
      createdAt: data?.created_at
    };
  } catch (error) {
    console.error('Error saving recommendation:', error);
    throw error;
  }
};

/**
 * Fetch user's recommendation history (per-account basis)
 * @param {string} userId - User ID
 * @param {number} limit - Number of recommendations to fetch
 * @returns {Promise<Array>} Array of recommendations
 */
export const fetchUserRecommendations = async (userId, limit = 50) => {
  try {
    const { data, error } = await supabase?.from('clinical_recommendations')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(limit);

    if (error) throw error;

    return data?.map(rec => ({
      id: rec?.id,
      userId: rec?.user_id,
      patientAge: rec?.patient_age,
      patientGender: rec?.patient_gender,
      bodyRegion: rec?.body_region,
      clinicalPresentation: rec?.clinical_presentation,
      recommendedModality: rec?.recommended_modality,
      reasoning: rec?.reasoning,
      confidenceScore: rec?.confidence_score,
      alternativeModalities: rec?.alternative_modalities,
      guidelinesReferenced: rec?.guidelines_referenced,
      creditsUsed: rec?.credits_used,
      feedbackRating: rec?.feedback_rating,
      feedbackNotes: rec?.feedback_notes,
      createdAt: rec?.created_at
    }));
  } catch (error) {
    console.error('Error fetching recommendations:', error);
    throw error;
  }
};

/**
 * Fetch single recommendation by ID
 * @param {string} recommendationId - Recommendation ID
 * @returns {Promise<Object>} Recommendation details
 */
export const fetchRecommendationById = async (recommendationId) => {
  try {
    const { data, error } = await supabase?.from('clinical_recommendations')?.select('*')?.eq('id', recommendationId)?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      patientAge: data?.patient_age,
      patientGender: data?.patient_gender,
      bodyRegion: data?.body_region,
      clinicalPresentation: data?.clinical_presentation,
      recommendedModality: data?.recommended_modality,
      reasoning: data?.reasoning,
      confidenceScore: data?.confidence_score,
      alternativeModalities: data?.alternative_modalities,
      guidelinesReferenced: data?.guidelines_referenced,
      creditsUsed: data?.credits_used,
      feedbackRating: data?.feedback_rating,
      feedbackNotes: data?.feedback_notes,
      createdAt: data?.created_at
    };
  } catch (error) {
    console.error('Error fetching recommendation:', error);
    throw error;
  }
};

/**
 * Delete recommendation from history
 * @param {string} recommendationId - Recommendation ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteRecommendation = async (recommendationId) => {
  try {
    const { error } = await supabase?.from('clinical_recommendations')?.delete()?.eq('id', recommendationId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting recommendation:', error);
    throw error;
  }
};

/**
 * Fetch all recommendations (admin only)
 * @param {Object} filters - Optional filters
 * @returns {Promise<Array>} Array of all recommendations
 */
export const fetchAllRecommendations = async (filters = {}) => {
  try {
    let query = supabase?.from('clinical_recommendations')?.select(`
        *,
        user_profiles!inner(email, full_name, institution_name, specialization)
      `)?.order('created_at', { ascending: false });

    if (filters?.userId) {
      query = query?.eq('user_id', filters?.userId);
    }

    if (filters?.bodyRegion) {
      query = query?.eq('body_region', filters?.bodyRegion);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data?.map(rec => ({
      id: rec?.id,
      userId: rec?.user_id,
      userEmail: rec?.user_profiles?.email,
      userFullName: rec?.user_profiles?.full_name,
      userInstitution: rec?.user_profiles?.institution_name,
      userSpecialty: rec?.user_profiles?.specialization,
      patientAge: rec?.patient_age,
      patientGender: rec?.patient_gender,
      bodyRegion: rec?.body_region,
      clinicalPresentation: rec?.clinical_presentation,
      recommendedModality: rec?.recommended_modality,
      reasoning: rec?.reasoning,
      confidenceScore: rec?.confidence_score,
      alternativeModalities: rec?.alternative_modalities,
      guidelinesReferenced: rec?.guidelines_referenced,
      creditsUsed: rec?.credits_used,
      feedbackRating: rec?.feedback_rating,
      feedbackNotes: rec?.feedback_notes,
      createdAt: rec?.created_at
    }));
  } catch (error) {
    console.error('Error fetching all recommendations:', error);
    throw error;
  }
};

// Legacy function for backward compatibility
export const getRecommendations = fetchUserRecommendations;